import { useLocation, useSearchParams } from 'react-router-dom';

export const AUTH_DEFAULT_NEXT = '/problems';
export const AUTH_FROM_STORAGE_KEY = 'sciplex.authFrom';

const AUTH_LOOP_RE = /^\/(login|register|forgot-password|reset-password|verify-email)(\/|$)/;

/** Относительный путь внутри сайта, без open-redirect. Совпадает с server/utils/safeNextPath.js */
export function safeNextPath(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return AUTH_DEFAULT_NEXT;
  let value = raw.trim();
  try {
    value = decodeURIComponent(value);
  } catch {
    return AUTH_DEFAULT_NEXT;
  }
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return AUTH_DEFAULT_NEXT;
  }
  if (value.includes('://')) return AUTH_DEFAULT_NEXT;
  const qIndex = value.indexOf('?');
  const pathOnly = qIndex === -1 ? value : value.slice(0, qIndex);
  const query = qIndex === -1 ? '' : value.slice(qIndex);
  if (AUTH_LOOP_RE.test(pathOnly)) return AUTH_DEFAULT_NEXT;
  const legacy = pathOnly.match(/^\/problem\/(\d+)$/);
  if (legacy) return `/problems/${legacy[1]}${query}`;
  return value;
}

export function currentReturnPath(pathname: string, search = ''): string {
  const suffix = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : '';
  return safeNextPath(`${pathname}${suffix}`);
}

export function authHref(base: string, from?: string | null): string {
  if (!from) return base;
  const safe = safeNextPath(from);
  const params = new URLSearchParams();
  params.set('from', safe);
  return `${base}?${params.toString()}`;
}

export function readAuthFrom(searchParams: URLSearchParams, stateFrom?: unknown): string {
  const fromQuery = searchParams.get('from');
  if (fromQuery) return safeNextPath(fromQuery);
  if (typeof stateFrom === 'string' && stateFrom) return safeNextPath(stateFrom);
  return AUTH_DEFAULT_NEXT;
}

export function rememberAuthFrom(path: string | null | undefined) {
  try {
    const safe = safeNextPath(path);
    if (!path || safe === AUTH_DEFAULT_NEXT) {
      sessionStorage.removeItem(AUTH_FROM_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(AUTH_FROM_STORAGE_KEY, safe);
  } catch {
    /* ignore */
  }
}

export function clearAuthFrom() {
  try {
    sessionStorage.removeItem(AUTH_FROM_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function consumeAuthFrom(): string {
  try {
    const stored = sessionStorage.getItem(AUTH_FROM_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_FROM_STORAGE_KEY);
    return safeNextPath(stored);
  } catch {
    return AUTH_DEFAULT_NEXT;
  }
}

export function useAuthNextPath(): string {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const stateFrom = (location.state as { from?: string } | null)?.from;
  return readAuthFrom(searchParams, stateFrom);
}
